<template>
  <el-col :span="24" ref="trxContainer">
    <el-row type="flex" justify="start" align="middle" class="v-padded-s trx-title-container" ref="trxTitleContainer">
      <el-col :span="isSaving && isSaveable ? 6 : 12" class="h-padded left-aligned-col">
        <el-row type="flex" align="middle" justify="start">
          <h2 style="margin: 0; font-weight: 100; display: inline-block;">NEW BILL PAYMENT</h2>
        </el-row>
      </el-col>
      <el-col :span="12" v-if="isSaving && isSaveable">
        <TransactionProgressBar
          :totalRequests="totalRequests"
          :completedRequests="completedRequests"
          :error="(!!failedLines.length)"
        />
      </el-col>
      <DataEntryButtons
        :isSaveable="isSaveable"
        :isOpenable="isOpenable"
        :isBalanced="transactionIsBalanced"
        :isSaving="isSaving"
        :transactionType="transactionType"
        @goBack="goBack"
        @submit="submitForm"
        @otherActions="otherActions"
        @dropdownClick="handleDropdownClick"
      />
    </el-row>
    <el-row type="flex" ref="mockTrxTitleContainer" />
    <el-row justify="start" align="start" type="flex" class="trx-header" ref="trxHeaderContainer">
      <el-col :span="8" style="margin-right: 10px">
        <el-row justify="start" align="start" type="flex">
          <HeaderFields
            title="Customer"
            :fields="fields.customer"
            :form="form"
            :idPrefix="'a-'"
            :modalOpen="dialogVisible"
            :isSaving="isSaving"
            @formChanged="updateForm"
          />
        </el-row>
        <el-row justify="start" align="middle" type="flex">
          <HeaderFields
            title="Batch"
            :fields="fields.batch"
            :form="form"
            :idPrefix="'b-'"
            :modalOpen="dialogVisible"
            :isSaving="isSaving"
            @formChanged="updateForm"
          />
        </el-row>
      </el-col>
      <el-col :span="8" style="margin: 0 10px;">
        <el-row justify="start" align="middle" type="flex">
          <HeaderFields
            title="Bill Payment Details"
            :fields="fields.bpDetails"
            :form="form"
            :idPrefix="'c-'"
            :modalOpen="dialogVisible"
            :isSaving="isSaving"
            @formatDateFields="formatDateFields"
            @formChanged="updateForm"
          />
        </el-row>
      </el-col>
      <el-col :span="8" style="margin-left: 10px;">
        <el-row justify="start" align="middle" type="flex">
          <HeaderFields
            title="Payment Info"
            :fields="fields.paymentInfo"
            :form="form"
            :lines="lines"
            :idPrefix="'d-'"
            :modalOpen="dialogVisible"
            :isSaving="isSaving"
            @formatDateFields="formatDateFields"
            @formChanged="updateForm"
          />
        </el-row>
      </el-col>
    </el-row>
    <el-row v-if="lines" justify="start" align="middle" type="flex" id="trx-lines-container">
      <DistributionLines
        v-if="dataLoaded"
        entityType="bp"
        ref="distribution-lines"
        :fields="lineFields"
        :existingLines="lines"
        :initLines="20"
        :modalOpen="dialogVisible"
        :parentForm="form"
        :clearFields="lineFieldsToClear"
        :reset="resetLines"
        :readOnly="isSaving"
        @lineModified="lineModified"
        @lineChanged="trackLineChanges"
        @linesHaveBeenChanged="setLinesHaveBeenChanged"
        @formatMoneyFields="formatLineMoneyField"
        @hasBeenReset="linesHaveBeenReset"
        @fieldsCleared="clearLineFieldsToClearList"
        @updateLines="updateLines"
        @mounted="setBatchAttributeLineValues"
      />
      <LinesLoading v-else :distLinesHeight="distLinesHeight" />
    </el-row>
    <el-dialog
      title="Confirm cancellation"
      :visible.sync="dialogVisible"
      size="tiny"
      :show-close="false"
      :close-on-press-escape="true"
      :close-on-click-modal="false">
      <span>Do you wish to save this transaction as a draft?</span>
      <span slot="footer" class="dialog-footer">
        <el-button
          class="exit-modal-btn"
          :disabled="isSaving"
          @click="cancelExit">
          Cancel
        </el-button>
        <el-button
          class="exit-modal-btn"
          type="primary"
          :disabled="isSaving"
          @click="goBack(true)">
          No
        </el-button>
        <el-button
          class="exit-modal-btn"
          type="primary"
          :disabled="isSaving"
          :loading="isSaving"
          @click="confirmSaveBeforeExit">
          Yes
        </el-button>
      </span>
    </el-dialog>
    <RouteChangeDialog :visible="showExitWarningDialog" @isVisible="handleExitWarningDialogVisible" @confirm="goBack(true)" />
    <NewBatch @populateFieldWithNewModel="populateFieldWithNewModel" />
    <CustomerVendorForm :type="customerVendorFormType" :modelId="modelId" @updateModelIdProp="updateVendorModelId" @populateFieldWithNewModel="populateFieldWithNewModel" />
  </el-col>
</template>

<script>
  /* eslint-disable camelcase */
  import { mapGetters } from 'vuex'
  import DistributionLines from '@/components/DistributionLinesNew.vue'
  import HeaderFields from '@/components/HeaderFields.vue'
  import CustomerVendorForm from '@/components/FormComponents/CustomerVendorForm.vue'
  import DataEntryButtons from '@/components/DataEntryButtons.vue'
  import NewBatch from '@/components/RouteComponents/Batches/NewBatch.vue'
  import { isTransactionBalanced } from '@/utils/helpers/DataEntryViews/IsTransactionBalanced'
  import { transactionMixins } from '@/Mixins/Transactions'
  import { transactionCreateViewMixins } from '@/Mixins/TransactionCreateViews'
  import { bpMixins } from '@/Mixins/BpMixins'
  import RouteChangeDialog from '@/components/RouteChangeDialog.vue'
  import TransactionProgressBar from '@/components/TransactionProgressBar.vue'
  import LinesLoading from '@/components/LinesLoading.vue'

  export default {
    components: {
      DistributionLines,
      HeaderFields,
      NewBatch,
      CustomerVendorForm,
      DataEntryButtons,
      RouteChangeDialog,
      TransactionProgressBar,
      LinesLoading
    },
    mixins: [ transactionMixins, transactionCreateViewMixins, bpMixins ],
    data () {
      const batch = this.setFormBatch()
      return {
        modelId: null,
        transactionType: { name: 'bp', path: '/bps/', prettyName: 'Bill Payment' },
        form: {
          customer: { label: '', value: '' },
          batch: { label: batch ? batch.name : '', value: batch ? batch.id : '' },
          number: '',
          date: '',
          description: '',
          period: '',
          amount_decimal: '',
          payment_method: 'CHK',
          payment_confirmation_number: '',
          lines: []
        },
        customerNumberVerifier: '', // TODO: this can go
        customerNumberCheckTimeout: null, // TODO: this can go
        currentCustomerSelection: null,
        onSubmitOpts: {},
        notificationBox: undefined
      }
    },
    methods: {
      /**
       * Updates modelId prop passed to customer create/edit modal
       */
      updateVendorModelId (id) {
        this.modelId = id
      },
      /**
       * Updates header form
       */
      updateForm (form, changedField = null) {
        this.form = { ...form }
        if (changedField === 'batch') {
          this.setBatchAttributeLineValues(this.batchAttributes)
        }
        if (this.form.customer.value) {
          this.currentCustomerSelection = this.form.customer.value
        }
        isTransactionBalanced(this)
        this.trackHeaderChanges()
        this.isSaveable = this.checkIfCanBeSaved()
        this.isOpenable = this.transactionIsBalanced && this.checkIfCanBeSaved()
      },
      /**
       * Updates distribution lines
       * @param lines
       */
      updateLines (lines) {
        if (lines) {
          this.lines = lines
          isTransactionBalanced(this)
          this.isSaveable = this.checkIfCanBeSaved()
          this.isOpenable = this.transactionIsBalanced && this.checkIfCanBeSaved()
        }
      },
      /**
       * Clears the list of line fields that should be cleared
       */
      clearLineFieldsToClearList () {
        this.lineFieldsToClear = []
      },
      /**
       * Resets the component's internal state
       */
      resetForm () {
        const batch = this.setFormBatch(this.form.batch.value)
        const form = {
          customer: { label: '', value: '' },
          batch: { label: batch ? batch.name : '', value: batch ? batch.id : '' },
          number: '',
          date: '',
          description: '',
          period: '',
          amount_decimal: '',
          payment_method: 'CHK',
          payment_confirmation_number: ''
        }
        this.form = { ...form }
        this.lineFieldsToClear = [] // TODO: this can go
        this.currentCustomerSelection = '' // TODO: this can go
        this.customerNumberVerifier = '' // TODO: this can go
        this.customerNumberCheckTimeout = null // TODO: this can go
      }
    },
    computed: {
      ...mapGetters({
        getSeriesById: 'getSeriesById',
        getLocationById: 'getLocationById',
        getSetById: 'getSetById',
        getTaxcodeById: 'getTaxcodeById',
        getFreefieldById: 'getFreefieldById',
        billableitems: 'getAllBillableitem',
        states: 'getAllStates'
      }),
      /**
       * Returns the header fields for this transaction type
       * @return Array
       */
      fields () {
        let fields = {
          customer: {
            inputFields: [
              {
                name: 'customer',
                label: 'Customer',
                dataType: 'string',
                fieldType: 'input',
                autocomplete: true,
                searchAsync: true,
                queryData: { endpoint: 'customers' },
                required: true,
                triggerOnFocus: false,
                msg: 'Please select a customer',
                addBtn: true
              }
            ],
            displayFields: [
              {
                name: 'customer',
                label: 'Type',
                value: this.form.customer.value ? this.getCustomerById(this.form.customer.value).entity_type : ''
              },
              {
                name: 'customer',
                label: 'Work State',
                value: this.customerWorkState
              }
            ]
          },
          batch: {
            inputFields: [
              {
                name: 'batch',
                label: 'Batch Name',
                dataType: 'number',
                fieldType: 'input',
                autocomplete: true,
                searchAsync: true,
                queryData: { endpoint: 'batches' },
                required: true,
                triggerOnFocus: false,
                msg: 'Please select a batch',
                addBtn: true
              }
            ],
            displayFields: [
              {
                name: 'batch',
                label: 'Company',
                value: this.batchAttributes.company ? this.batchAttributes.company.name : ''
              },
              {
                name: 'batch',
                label: 'Production',
                value: this.batchAttributes.production ? this.batchAttributes.production.name : ''
              },
              {
                name: 'batch',
                label: 'Bank',
                value: this.batchAttributes.bank ? this.batchAttributes.bank.name : ''
              },
              {
                name: 'batch',
                label: 'Currency',
                value: this.batchAttributes.currency ? this.batchAttributes.currency.code : ''
              }
            ]
          },
          bpDetails: {
            inputFields: [
              {
                name: 'number',
                label: 'Number',
                dataType: 'string',
                fieldType: 'input',
                required: true
              },
              {
                name: 'date',
                label: 'Date',
                dataType: 'string',
                fieldType: 'date',
                required: true,
                msg: 'Please specify a date'
              },
              {
                name: 'description',
                label: 'Description',
                dataType: 'string',
                fieldType: 'input',
                required: true,
                msg: 'Please provide a description',
                min: 3
              },
              {
                name: 'period',
                label: 'Period',
                dataType: 'number',
                fieldType: 'select',
                options: [
                  { label: 'Next', value: '', selected: !(this.currentPeriod && this.currentPeriod.id) }
                ]
              }
            ]
          },
          paymentInfo: {
            inputFields: [
              {
                name: 'amount_decimal',
                label: 'Amount',
                dataType: 'string',
                fieldType: 'money',
                required: true,
                isMoney: true
              },
              {
                name: 'payment_method',
                label: 'Payment type',
                dataType: 'string',
                fieldType: 'select',
                required: true,
                options: [
                  { label: 'Check', value: 'CHK' },
                  { label: 'Cash', value: 'CSH' },
                  { label: 'Automated Clearing House', value: 'ACH' }
                ]
              },
              {
                name: 'payment_confirmation_number',
                label: 'Payment number',
                dataType: 'string',
                fieldType: 'input'
              }
            ]
          }
        }
        if (this.currentPeriod && this.currentPeriod.id) {
          fields.bpDetails.inputFields[3].options.push({
            label: 'Current',
            value: this.currentPeriod.id,
            selected: true
          })
        }
        return fields
      }
    },
    watch: {
      currentCustomerSelection () {
        if (this.currentCustomerSelection) {
          this.setDefaultPaymentType()
        }
      }
    }
  }
</script>

<style scoped>
  .h-padded {
    padding: 0 12px;
  }
  .v-padded-s {
    padding-top: 12px;
    padding-bottom: 12px;
  }
  .left-aligned-col {
    text-align: left;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
  .el-form-item__label {
    padding: 20px !important;
    border: 1px solid pink;
  }
  .el-row.header {
    padding: 0 20px;
  }
  .el-carousel__item {
    overflow-y: scroll;
  }
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Bps/New.vue